import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Building2, Users, Briefcase, Search, Settings, LogOut, Plus } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { LanguageSelector } from "@/components/LanguageSelector";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTranslations } from "@/translations";
import { HeaderSearchBar } from "@/components/HeaderSearchBar";
import { OptimizedImage } from "@/components/ui/optimized-image";
import logoAsset from "@/assets/greatjobs-logo.png.asset.json";

const Header = () => {
  const { user, profile, signOut } = useAuth();
  const { language } = useLanguage();
  const t = useTranslations(language);
  const navigate = useNavigate();
  
  const isEmployer = profile?.user_type === 'employer';

  const handleSignOut = async () => {
    await signOut();
  };

  return (
    <nav className="bg-white/95 backdrop-blur-sm border-b border-border sticky top-0 z-50">
      <div className="container mx-auto px-4 h-16 flex items-center justify-between gap-4">
        {/* Logo */}
        <Link to="/" className="flex items-center space-x-2 flex-shrink-0">
          <OptimizedImage 
            src={logoAsset.url}
            alt="GreatJobs.lk" 
            width={219}
            height={48}
            className="h-12 w-auto"
            priority
          />
        </Link>
        
        {/* Persistent Search Bar - always visible */}
        <div className="hidden md:flex max-w-sm ml-4">
          <HeaderSearchBar />
        </div>
        
        
        {user ? (
          <div className="flex items-center space-x-4">
            {/* Only show employer functions if user is an employer */}
            {isEmployer && (
              <div className="hidden lg:flex items-center space-x-4">
                <Button variant="ghost" size="sm" asChild className="flex items-center space-x-2">
                  <Link to="/ats">
                    <Users className="h-4 w-4" />
                    <span>{t.nav.ats}</span>
                  </Link>
                </Button>
                <Button variant="ghost" size="sm" asChild className="flex items-center space-x-2">
                  <Link to="/job-management">
                    <Briefcase className="h-4 w-4" />
                    <span>{t.nav.jobPostings}</span>
                  </Link>
                </Button>
                <Button variant="ghost" size="sm" asChild className="flex items-center space-x-2">
                  <Link to="/candidate-search">
                    <Search className="h-4 w-4" />
                    <span>{t.nav.candidates}</span>
                  </Link>
                </Button>
              </div>
            )}
            
            <div className="flex items-center space-x-2">
              <LanguageSelector />
              <Button variant="ghost" size="icon" asChild>
                <Link to={`/account-settings?tab=${isEmployer ? 'company' : 'profile'}`}>
                  <Settings className="h-4 w-4" />
                </Link>
              </Button>
              <Button variant="ghost" size="sm" onClick={handleSignOut}>
                <LogOut className="w-4 h-4 mr-2" />
                {t.nav.signOut}
              </Button>
              {/* Primary CTAs */}
              {isEmployer && (
                <Button 
                  size="sm" 
                  className="bg-secondary hover:bg-secondary/90 text-secondary-foreground shadow-md hover:shadow-lg transition-all duration-200 font-medium" 
                  onClick={() => navigate('/post-job')}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  {t.nav.postJob}
                </Button>
              )}
            </div>
          </div>
        ) : (
          /* Navigation for Non-logged in users */
          <div className="flex items-center space-x-3">
            <LanguageSelector />
            
            {/* Primary CTAs - Reordered for better hierarchy */}
            <Button 
              size="sm" 
              className="bg-secondary hover:bg-secondary/90 text-secondary-foreground shadow-md hover:shadow-lg transition-all duration-200 font-medium border border-secondary/20 min-h-[44px]" 
              onClick={() => navigate('/auth?signup=true')}
            >
              <Plus className="w-4 h-4 mr-1" />
              Post 3 Jobs Free
            </Button>
            
            <Button variant="default" size="sm" className="min-h-[44px]" onClick={() => navigate('/jobs')}>
              <Search className="w-4 h-4 mr-1" />
              Find Jobs
            </Button>
            
            <Button variant="ghost" size="sm" className="min-h-[44px]" onClick={() => navigate('/auth')}>
              Log In
            </Button>
          </div>
        )}
      </div>
      
      {/* Mobile Search Bar */}
      <div className="md:hidden border-t border-border/50 p-4">
        <HeaderSearchBar />
      </div>
    </nav>
  );
};

export default Header;